import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { TILE_URL, TILE_ATTR } from "@/lib/mapTiles";

const COLORS = ["#1E3A8A", "#B91C1C", "#047857", "#B45309", "#6D28D9", "#0E7490", "#BE185D", "#4338CA"];

function numIcon(n, color) {
  return L.divIcon({ className: "", iconSize: [24, 24], iconAnchor: [12, 12],
    html: `<div style="width:24px;height:24px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);color:#fff;font-size:11px;font-weight:900;display:flex;align-items:center;justify-content:center">${n}</div>` });
}

// days: [{date, geometry:[[lat,lng]], points:[{lat,lng,order,name}]}]
export default function HistoryMap({ days = [], homeBase, heightClass = "h-[55vh] min-h-[340px]", testId = "history-map" }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !el.current) return;
    const map = L.map(el.current, { zoomAnimation: false }).setView([25.3, 55.4], 11);
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);
    layerRef.current = new L.FeatureGroup().addTo(map);
    mapRef.current = map;
    const t = setTimeout(() => mapRef.current && map.invalidateSize(), 250);
    return () => { clearTimeout(t); map.stop(); map.off(); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const allPts = [];

    if (homeBase?.latitude != null) {
      L.marker([homeBase.latitude, homeBase.longitude], {
        icon: L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 13],
          html: `<div style="width:26px;height:26px;border-radius:7px;background:#111827;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);color:#fff;display:flex;align-items:center;justify-content:center"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg></div>` }),
      }).addTo(layer).bindPopup("Home base");
      allPts.push([homeBase.latitude, homeBase.longitude]);
    }

    days.forEach((d, di) => {
      const color = COLORS[di % COLORS.length];
      const line = (d.geometry && d.geometry.length >= 2) ? d.geometry
        : (d.points || []).map((p) => [p.lat, p.lng]);
      if (line.length >= 2) {
        L.polyline(line, { color, weight: 4, opacity: 0.75 }).addTo(layer);
        line.forEach((p) => allPts.push(p));
      }
      (d.points || []).forEach((p) => {
        if (p.lat == null) return;
        L.marker([p.lat, p.lng], { icon: numIcon(p.order, color) }).addTo(layer)
          .bindPopup(`<b>${p.order}. ${p.name || ""}</b><br/>${d.date}`);
        allPts.push([p.lat, p.lng]);
      });
    });

    if (allPts.length) {
      const b = L.latLngBounds(allPts);
      if (b.isValid()) map.fitBounds(b, { padding: [30, 30], maxZoom: 15, animate: false });
    }
    const t = setTimeout(() => mapRef.current && map.invalidateSize(), 150);
    return () => clearTimeout(t);
  }, [days, homeBase]);

  return <div ref={el} data-testid={testId} className={`w-full ${heightClass} rounded-xl border border-[#E5E7EB] z-0`} />;
}
